import { useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const AI_TOOLS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-tools`;

export type AIToolType = 'video-prompt' | 'ad-copy' | 'marketing-plan' | 'niche-strategy' | 'launch-strategy';


export const useAITool = () => {
  const [result, setResult] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const generate = async (
    type: AIToolType,
    input: string,
    isPaid: boolean,
    onCreditsUpdate?: (newBalance: number) => void,
    options?: Record<string, string>,
  ) => {
    setResult('');
    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

      const resp = await fetch(AI_TOOLS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ type, input, isPaid, options: options || {} }),
      });

      if (resp.status === 429) {
        toast({ title: 'Rate limited', description: 'Please wait a moment and try again.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (resp.status === 402) {
        toast({ title: 'Credits exhausted', description: 'AI credits are temporarily unavailable.', variant: 'destructive' });
        setIsLoading(false);
        return;
      }
      if (resp.status === 403) {
        const data = await resp.json();
        if (data.error === 'insufficient_credits') {
          toast({
            title: 'Not enough credits',
            description: `You need ${data.required} credits but only have ${data.balance}. Upgrade your plan for more credits.`,
            variant: 'destructive',
          });
          setIsLoading(false);
          return;
        }
      }
      if (!resp.ok || !resp.body) throw new Error('Failed to start stream');

      // Read remaining credits from response header
      const remainingCredits = resp.headers.get('X-Credits-Remaining');
      if (remainingCredits && onCreditsUpdate) {
        onCreditsUpdate(parseInt(remainingCredits, 10));
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = '';
      let fullText = '';
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) break;
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || line.trim() === '') continue;
          if (!line.startsWith('data: ')) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') {
            streamDone = true;
            break;
          }

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              fullText += content;
              setResult(fullText);
            }
          } catch {
            textBuffer = line + '\n' + textBuffer;
            break;
          }
        }
      }

      // Final flush
      if (textBuffer.trim()) {
        for (let raw of textBuffer.split('\n')) {
          if (!raw) continue;
          if (raw.endsWith('\r')) raw = raw.slice(0, -1);
          if (raw.startsWith(':') || raw.trim() === '') continue;
          if (!raw.startsWith('data: ')) continue;
          const jsonStr = raw.slice(6).trim();
          if (jsonStr === '[DONE]') continue;
          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            if (content) {
              fullText += content;
              setResult(fullText);
            }
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      console.error('AI tool error:', e);
      toast({ title: 'Error', description: 'Something went wrong. Please try again.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const clear = () => setResult('');

  return { result, isLoading, generate, clear };
};
